'use client';

import { apiClient } from '@/lib/api-client';
import {
  clearAuthTokens,
  decodeAccessToken,
  decodeTokenClaims,
  getUserRole,
  TokenClaims,
  User,
  UserRole,
} from '@/lib/auth';
import { encodeOrgCode } from '@/lib/org-code';
import keycloak from '@/lib/keycloak';
import { buildAuthScope, resolveEntryOrg } from '@/lib/tenant';
import { useCompanyStore } from '@/stores/company-store';
import { useFilterStore } from '@/stores/filter-store';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

// Keycloak은 한 번만 초기화되어야 함 (React StrictMode 대응)
let keycloakInitialized = false;

interface AuthContextType {
  user: User | null;
  userRole: UserRole | null;
  tokenClaims: TokenClaims | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  switchCompany: (compCd: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  // 편의 함수들
  isSuperAdmin: boolean;
  getUserLevel: () => 'COMPANY' | 'ORGANIZATION' | 'BUSINESS' | 'STORE' | null;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  getRoles: () => string[];
  getCompanyCode: () => string | null;
  getOrganizationCode: () => string | null;
  // RBAC용 uid 함수
  getCurrentUserId: () => number | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

// Keycloak 토큰에서 사용자 정보 추출
function extractUserFromToken(token: string): User | null {
  return decodeAccessToken(token);
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [tokenClaims, setTokenClaims] = useState<TokenClaims | null>(null);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [profileSuperAdmin, setProfileSuperAdmin] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isAuthenticated = !!user;
  // superAdmin: 프로필 플래그 우선, 없으면 토큰(realm_access.roles)의 SUPER_ADMIN 폴백
  const isSuperAdmin = profileSuperAdmin ?? userRole?.isSuperAdmin ?? false;

  // 토큰에서 상태 동기화
  const syncStateFromToken = useCallback((token: string) => {
    const currentUser = extractUserFromToken(token);
    const currentTokenClaims = decodeTokenClaims(token);
    const currentUserRole = currentTokenClaims
      ? getUserRole(token)
      : null;

    setUser(currentUser);
    setUserRole(currentUserRole);
    setTokenClaims(currentTokenClaims);
  }, []);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const refreshed = await keycloak.updateToken(30);
      if (refreshed && keycloak.token) {
        syncStateFromToken(keycloak.token);
      }
      return true;
    } catch (error) {
      console.error('Token refresh error:', error);
      return false;
    }
  }, [syncStateFromToken]);

  useEffect(() => {
    // React StrictMode에서 중복 초기화 방지
    if (keycloakInitialized) return;
    keycloakInitialized = true;

    // Keycloak 초기화 — 미인증 시 Keycloak 로그인 페이지로 즉시 리다이렉트
    keycloak
      .init({
        onLoad: 'login-required',
        pkceMethod: 'S256',
        checkLoginIframe: false,
        // ?org={compCd}로 지정된 테넌트를 로그인에 고정 (Keycloak Organizations)
        scope: buildAuthScope(resolveEntryOrg()),
      })
      .then(async authenticated => {
        if (authenticated && keycloak.token) {
          syncStateFromToken(keycloak.token);
          try {
            const profileData = await apiClient.get('profile').json<any>();
            // 백엔드 응답이 { result: { userId } } 또는 { userId } 두 형태 모두 처리
            const userId: number | null =
              profileData?.result?.userId ?? profileData?.userId ?? null;
            setCurrentUserId(userId);
            const profileSuperAdminFlag: boolean | null =
              profileData?.result?.superAdmin ?? profileData?.superAdmin ?? null;
            setProfileSuperAdmin(profileSuperAdminFlag);
          } catch (error) {
            console.error('프로필 조회 오류:', error);
          }
        }
      })
      .catch(error => {
        console.error('Keycloak 초기화 오류:', error);
        setUser(null);
      })
      .finally(() => {
        setIsLoading(false);
      });

    // 토큰 만료 시 자동 갱신
    keycloak.onTokenExpired = () => {
      keycloak.updateToken(30).then(refreshed => {
        if (refreshed && keycloak.token) {
          syncStateFromToken(keycloak.token);
        }
      }).catch(() => {
        // 갱신 실패 시 로그인으로 리다이렉트
        keycloak.login({ scope: buildAuthScope(resolveEntryOrg()) });
      });
    };

    // 30초마다 토큰 만료 여부 확인 및 갱신
    refreshIntervalRef.current = setInterval(() => {
      if (keycloak.authenticated) {
        keycloak.updateToken(30).then(refreshed => {
          if (refreshed && keycloak.token) {
            syncStateFromToken(keycloak.token);
          }
        }).catch(() => {
          // 갱신 실패 무시 (onTokenExpired에서 처리)
        });
      }
    }, 30000);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [syncStateFromToken]);

  // Keycloak 로그인 페이지로 리다이렉트
  const login = async () => {
    await keycloak.login({ scope: buildAuthScope(resolveEntryOrg()) });
  };

  // 활성 회사(compCd) 전환: 현재 토큰은 이전 조직 스코프로 발급되어 있으므로
  // client navigate가 아니라 organization:{compCd} 스코프로 전체 재로그인해야
  // 토큰이 새 조직으로 재발급된다(KC 세션이 있으면 SSO로 프롬프트 없이 통과).
  const switchCompany = useCallback((compCd: string) => {
    // tenant.ts의 LAST_ORG_KEY('petrohq.lastOrg')와 반드시 동일한 키여야
    // resolveEntryOrg의 재방문 폴백이 이 값을 읽는다.
    window.localStorage.setItem('petrohq.lastOrg', compCd);
    return keycloak.login({
      scope: buildAuthScope(compCd),
      redirectUri: window.location.origin + '/' + encodeOrgCode(compCd),
    });
  }, []);

  const logout = async () => {
    try {
      // keycloak.logout()은 페이지를 리다이렉트하므로
      // 리다이렉트 전에 로컬 상태를 모두 정리해야 함
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      clearAuthTokens();
      useCompanyStore.getState().resetCompanyState();
      useFilterStore.getState().resetFilters();
      setUser(null);
      setUserRole(null);
      setTokenClaims(null);
      setCurrentUserId(null);
      setProfileSuperAdmin(null);

      await keycloak.logout({
        redirectUri: window.location.origin + '/',
      });
    } catch (error) {
      console.error('Logout error:', error);
      window.location.href = window.location.origin;
    }
  };

  // 편의 함수들
  const getUserLevel = useCallback(():
    | 'COMPANY'
    | 'ORGANIZATION'
    | 'BUSINESS'
    | 'STORE'
    | null => {
    return userRole?.userLevel || null;
  }, [userRole]);

  const hasRole = useCallback(
    (role: string): boolean => {
      return userRole?.hasRole(role) || false;
    },
    [userRole]
  );

  const hasAnyRole = useCallback(
    (roles: string[]): boolean => {
      return userRole?.hasAnyRole(roles) || false;
    },
    [userRole]
  );

  const getRoles = useCallback((): string[] => {
    return userRole?.roles || [];
  }, [userRole]);

  const getCompanyCode = useCallback((): string | null => {
    return tokenClaims?.companyCode || null;
  }, [tokenClaims]);

  const getOrganizationCode = useCallback((): string | null => {
    return tokenClaims?.organizationCode || null;
  }, [tokenClaims]);

  const getCurrentUserId = useCallback((): number | null => {
    return currentUserId;
  }, [currentUserId]);

  const value: AuthContextType = {
    user,
    userRole,
    tokenClaims,
    isAuthenticated,
    isLoading,
    login,
    switchCompany,
    logout,
    refreshToken,
    isSuperAdmin,
    getUserLevel,
    hasRole,
    hasAnyRole,
    getRoles,
    getCompanyCode,
    getOrganizationCode,
    getCurrentUserId,
  };

  // Keycloak 초기화 완료 전에는 자식 렌더링을 차단하여
  // 하위 Provider들의 API 호출이 토큰 없이 실행되는 것을 방지
  if (isLoading) {
    return (
      <AuthContext.Provider value={value}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <span>인증 확인 중...</span>
        </div>
      </AuthContext.Provider>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
